import { api } from '@/services/api/AxiosBaseService'
import type { PaginatedResponse } from '@/types/api'

export interface Bank             { id: string; code: string; name: string; fullName: string; status: string }
export interface MaritalStatus    { id: string; maritalStatus: string; description: string; status: string }
export interface DegreeRelation   { id: string; degreeRelationship: string; code: string; gender: string; status: string }
export interface State            { id: string; state: string; abbreviation: string }
export interface City             { id: string; city: string; stateId?: string; state?: State }
export interface PlanTypeRelation { id: string; name: string; status: string }

function listFactory<T>(path: string) {
  return async (size = 100) => {
    const r = await api.get<PaginatedResponse<T>>(path, { params: { size } })
    return r.data.data
  }
}

export const LookupService = {
  banks:              listFactory<Bank>('/settings/banks'),
  maritalStatuses:    listFactory<MaritalStatus>('/settings/marital-statuses'),
  degreeRelationships: listFactory<DegreeRelation>('/settings/degree-relationships'),
  planTypeRelations:  listFactory<PlanTypeRelation>('/settings/plan-type-relationships'),
  states:             listFactory<State>('/locations/states'),

  /**
   * Lista cidades. Quando `stateId` é fornecido, filtra no backend (carregar
   * todas as ~5570 cidades de uma vez é desperdício). Sem `stateId` retorna
   * até `size` cidades (default 0 = todas — usar com cuidado).
   */
  async cities(stateId?: string, size = 0): Promise<City[]> {
    const params: Record<string, unknown> = { size }
    if (stateId) params.state_id = stateId
    const r = await api.get<PaginatedResponse<City>>('/locations/cities', { params })
    return r.data.data
  },

  /**
   * Consulta CNPJ via BrasilAPI (proxy backend). Retorna dados cadastrais
   * normalizados para auto-preencher formulários de empresa.
   * Retorna `null` quando o CNPJ não é encontrado ou erro de rede.
   */
  async lookupCnpj(cnpj: string): Promise<{
    razaoSocial: string
    nomeFantasia: string
    address: string
    number: string
    complement: string
    district: string
    cityName: string
    stateAbbr: string
    postalCode: string
    email: string
    phone: string
  } | null> {
    const cleaned = cnpj.replace(/\D+/g, '')
    if (cleaned.length !== 14) return null
    try {
      const r = await api.get<{ data: {
        razaoSocial: string; nomeFantasia: string;
        address: string; number: string; complement: string; district: string;
        cityName: string; stateAbbr: string; postalCode: string;
        email: string; phone: string;
      } }>(`/locations/cnpj/${cleaned}`)
      return r.data.data
    } catch {
      return null
    }
  },

  /**
   * Resolve uma cidade pelo nome + UF. Cria no banco se não existir
   * (idempotente). Usado pelo autofill de CEP — a base inicial de cidades
   * é mínima e cresce conforme novos endereços são cadastrados.
   */
  async findOrCreateCity(cityName: string, stateAbbr: string): Promise<City> {
    const r = await api.post<{ data: City }>('/locations/cities/find-or-create', {
      city:       cityName,
      state_abbr: stateAbbr,
    })
    return r.data.data
  },
}
